type Json = Record<string, unknown> | unknown[] | string | number | boolean | null

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function mergeValues(base: Json, override: Json): Json {
  if (Array.isArray(base) && Array.isArray(override)) {
    return override
  }

  if (isRecord(base) && isRecord(override)) {
    const result: Record<string, Json> = {}
    const keys = new Set<string>([...Object.keys(base), ...Object.keys(override)])
    for (const key of keys) {
      const baseValue = base[key] as Json
      const overrideValue = override[key] as Json | undefined
      result[key] = overrideValue === undefined ? baseValue : mergeValues(baseValue, overrideValue)
    }
    return result
  }

  return override ?? base
}

export function mergeCatalogRules(baseRules: unknown, overrides: unknown): Json {
  if (isRecord(baseRules)) {
    if (isRecord(overrides)) {
      return mergeValues(baseRules as Json, overrides as Json)
    }
    return baseRules as Json
  }

  if (isRecord(overrides) || Array.isArray(overrides)) {
    return overrides as Json
  }

  if (
    typeof overrides === 'string' ||
    typeof overrides === 'number' ||
    typeof overrides === 'boolean' ||
    overrides === null
  ) {
    return overrides
  }

  if (
    Array.isArray(baseRules) ||
    typeof baseRules === 'string' ||
    typeof baseRules === 'number' ||
    typeof baseRules === 'boolean' ||
    baseRules === null
  ) {
    return baseRules as Json
  }

  return null
}

export function normalizeAliases(
  value: string | string[] | undefined | null
): string[] | undefined {
  if (!value) return undefined
  const list = Array.isArray(value) ? value : value.split(',')
  const cleaned = list.map(item => item.trim()).filter(item => item.length > 0)
  return cleaned.length > 0 ? cleaned : undefined
}

export function safeJsonParse<T>(value: string, fallback: T): T {
  try {
    return JSON.parse(value) as T
  } catch {
    return fallback
  }
}
